import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';
import { api } from 'src/app/models/api-url.model';
import { Ticket } from 'src/app/models/ticket/ticket.model';
import { isTicketSupport } from 'src/app/models/ticket/ticket.util';
import { ApiService } from 'src/app/core/services/api';
import {
  HistoricalObservation,
  SynapseService,
} from 'src/app/core/services/synapse';
import { HistoricalObservationsModalComponent } from '../historical-observations-modal/historical-observations-modal.component';

@Component({
  selector: 'app-ticket-iq-observation',
  templateUrl: './ticket-iq-observation.component.html',
  styleUrls: ['./ticket-iq-observation.component.scss'],
  standalone: false,
})
export class TicketIqObservationComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() ticket: Ticket;

  isLoading: boolean = false;

  isDataLoaded: boolean = false;

  summary: string = null;

  private destroy$ = new Subject<void>();

  historicalObservations: HistoricalObservation[] = [];

  private iqService = inject(SynapseService);

  private apiService = inject(ApiService);

  private cdr = inject(ChangeDetectorRef);

  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ticket && changes.ticket.currentValue) {
      this.isDataLoaded = false;
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openSummaryModal(serviceName: string): void {
    this.dialog.open(HistoricalObservationsModalComponent, {
      width: '80vw',
      maxHeight: '90vh',
      data: {
        observations: this.historicalObservations,
        serviceName,
      },
    });
  }

  loadHistoricalObservations(nit: string): void {
    const url = `${api.getHistoricalObservationsByNIT}/${nit}`;
    this.apiService
      .get(url)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.historicalObservations = Array.isArray(data?.observations)
            ? data?.observations.map((obs) => ({
                ...obs,
                date: this.formatDate(obs.date),
                begin_time: this.formatTime(obs.begin_time),
                end_time: this.formatTime(obs.end_time),
              }))
            : [];
          this.cdr.detectChanges();
        },
        error: () => {
          this.historicalObservations = [];
          this.cdr.detectChanges();
        },
      });
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${month}/${day}/${year}`;
  }

  private formatTime(timeString: string): string {
    return timeString.slice(0, 5);
  }

  loadData(): void {
    this.isLoading = true;

    if (!isTicketSupport(this.ticket)) {
      this.isDataLoaded = true;
      this.isLoading = false;
      this.summary = null;
      this.historicalObservations = [];
      this.cdr.detectChanges();
      return;
    }

    if (this.ticket?.Service__r?.Name) {
      this.loadHistoricalObservations(this.ticket.Service__r.Name);
    }

    this.iqService
      .getIQTicketSummary(this.ticket.CaseNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.isDataLoaded = true;
          this.isLoading = false;
          this.summary = (data?.[0]?.summary as string) || null;
          this.cdr.detectChanges();
        },
        error: () => {
          this.isDataLoaded = true;
          this.isLoading = false;
          this.summary = null;
          this.cdr.detectChanges();
        },
      });
  }
}
